import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { fmtDate, fmtDist, fmtDur } from "@/lib/api";
import { Printer, ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";

const mondayOf = (d) => {
  const dt = new Date(d + "T00:00:00");
  dt.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));
  return dt.toISOString().slice(0, 10);
};
const shiftWeek = (iso, days) => {
  const dt = new Date(iso + "T00:00:00");
  dt.setDate(dt.getDate() + days);
  return dt.toISOString().slice(0, 10);
};
const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;

const Section = ({ title, children }) => (
  <div className="mt-6">
    <div className="uppercase text-[10px] tracking-[0.3em] font-bold bg-black text-white px-3 py-1.5 inline-block">{title}</div>
    {children}
  </div>
);
const TH = ({ children }) => <th className="py-2 pr-3 uppercase text-[9px] tracking-[0.2em] font-bold text-left border-b-2 border-black">{children}</th>;
const TD = ({ children, cls = "", colSpan }) => <td colSpan={colSpan} className={`py-2 pr-3 border-b border-neutral-300 ${cls}`}>{children}</td>;

export default function WeeklyReportPage() {
  const [params, setParams] = useSearchParams();
  const week = params.get("week") || mondayOf(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/reports/weekly?week_start=${week}`).then((r) => setData(r.data)).catch(() => {});
  }, [week]);

  const setWeek = (w) => setParams({ week: w });

  if (!data) return <div className="min-h-screen bg-background text-muted-foreground flex items-center justify-center">Loading…</div>;

  const t = data.totals;

  return (
    <div className="min-h-screen bg-background print:bg-white py-8 print:py-0">
      <div className="max-w-[210mm] mx-auto mb-4 flex justify-between items-center px-2 print:hidden">
        <Link to="/" data-testid="report-back-link" className="text-muted-foreground text-xs uppercase tracking-widest inline-flex gap-2 items-center hover:text-foreground">
          <ArrowLeft size={14} /> Dashboard
        </Link>
        <div className="flex items-center gap-2">
          <button data-testid="report-prev-week" onClick={() => setWeek(shiftWeek(week, -7))} className="border border-border px-2.5 py-2 hover:bg-muted text-foreground"><ChevronLeft size={14} /></button>
          <button data-testid="report-next-week" onClick={() => setWeek(shiftWeek(week, 7))} className="border border-border px-2.5 py-2 hover:bg-muted text-foreground"><ChevronRight size={14} /></button>
          <button data-testid="print-report-button" onClick={() => window.print()} className="bg-[#FF3B30] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#ff554b] inline-flex items-center gap-2">
            <Printer size={14} /> Print report
          </button>
        </div>
      </div>

      <div className="max-w-[210mm] mx-auto bg-white text-black p-10 print:p-4 text-sm" data-testid="weekly-report">
        <div className="flex justify-between items-start border-b-4 border-black pb-4">
          <div>
            <div className="font-heading font-black text-3xl tracking-tighter leading-none">
              LEO'S<span className="text-[#FF3B30]">FLEET</span>
            </div>
            <div className="uppercase text-[10px] tracking-[0.3em] font-bold text-neutral-500 mt-1">Weekly Fleet Report</div>
          </div>
          <div className="text-right font-bold">
            {fmtDate(data.week_start)} — {fmtDate(data.week_end)}
          </div>
        </div>

        <div className="grid grid-cols-6 gap-3 py-4 border-b border-neutral-300">
          {[
            ["Routes", t.routes],
            ["Distance", fmtDist(t.distance_m)],
            ["Drive time", fmtDur(t.duration_s)],
            ["Services", gbp(t.services_cost)],
            ["Fines", gbp(t.fines_total)],
            ["Alerts", `${t.alerts} (${t.expired} expired)`],
          ].map(([l, v]) => (
            <div key={l}>
              <div className="uppercase text-[9px] tracking-[0.2em] font-bold text-neutral-500">{l}</div>
              <div className="font-heading font-black text-lg tracking-tighter">{v}</div>
            </div>
          ))}
        </div>

        <Section title="Compliance alerts — next 7 days">
          <table className="w-full mt-2">
            <thead><tr><TH>Reg</TH><TH>Driver</TH><TH>Type</TH><TH>Due</TH><TH>Status</TH></tr></thead>
            <tbody>
              {data.reminders.map((r, i) => (
                <tr key={i}>
                  <TD cls="font-mono font-bold">{r.registration}</TD>
                  <TD>{r.driver_name || "—"}</TD>
                  <TD>{r.type}</TD>
                  <TD>{fmtDate(r.due_date)}</TD>
                  <TD cls={r.severity === "expired" ? "text-[#FF3B30] font-bold uppercase" : "font-bold uppercase"}>
                    {r.days_left < 0 ? `${-r.days_left}d overdue` : r.days_left === 0 ? "Due today" : `${r.days_left}d left`}
                  </TD>
                </tr>
              ))}
              {data.reminders.length === 0 && <tr><TD cls="text-neutral-500" colSpan={5}>All clear — no expiries within 7 days.</TD></tr>}
            </tbody>
          </table>
        </Section>

        <Section title="Routes this week">
          <table className="w-full mt-2">
            <thead><tr><TH>Date</TH><TH>Vehicle</TH><TH>Driver</TH><TH>Stops</TH><TH>Distance</TH><TH>Drive time</TH></tr></thead>
            <tbody>
              {data.routes.map((r) => (
                <tr key={r.id}>
                  <TD>{fmtDate(r.date)}</TD>
                  <TD cls="font-mono font-bold">{r.vehicle_reg}</TD>
                  <TD>{r.driver_name || "—"}</TD>
                  <TD>{r.stops?.length ?? 0}</TD>
                  <TD>{fmtDist(r.total_distance_m)}</TD>
                  <TD>{fmtDur(r.total_duration_s)}</TD>
                </tr>
              ))}
              {data.routes.length === 0 && <tr><TD cls="text-neutral-500" colSpan={6}>No routes planned this week.</TD></tr>}
            </tbody>
          </table>
        </Section>

        <Section title="Services & repairs">
          <table className="w-full mt-2">
            <thead><tr><TH>Date</TH><TH>Vehicle</TH><TH>Description</TH><TH>Garage</TH><TH>Cost</TH></tr></thead>
            <tbody>
              {data.services.map((s) => (
                <tr key={s.id}>
                  <TD>{fmtDate(s.date)}</TD>
                  <TD cls="font-mono font-bold">{s.vehicle_reg}</TD>
                  <TD>{s.description}</TD>
                  <TD>{s.garage || "—"}</TD>
                  <TD cls="font-bold">{s.cost != null ? gbp(s.cost) : "—"}</TD>
                </tr>
              ))}
              {data.services.length === 0 && <tr><TD cls="text-neutral-500" colSpan={5}>No services recorded this week.</TD></tr>}
            </tbody>
          </table>
        </Section>

        <Section title="PCN fines">
          <table className="w-full mt-2">
            <thead><tr><TH>Date</TH><TH>Reg</TH><TH>Driver</TH><TH>Company</TH><TH>Reference</TH><TH>Amount</TH><TH>Status</TH></tr></thead>
            <tbody>
              {data.fines.map((f) => (
                <tr key={f.id}>
                  <TD>{fmtDate(f.date)}</TD>
                  <TD cls="font-mono font-bold">{f.vehicle_reg}</TD>
                  <TD>{f.driver_name || "—"}</TD>
                  <TD>{f.issuer || "—"}</TD>
                  <TD cls="font-mono">{f.reference}</TD>
                  <TD cls="font-bold">{gbp(f.amount)}</TD>
                  <TD cls="uppercase font-bold">{f.status}</TD>
                </tr>
              ))}
              {data.fines.length === 0 && <tr><TD cls="text-neutral-500" colSpan={7}>No fines issued this week.</TD></tr>}
            </tbody>
          </table>
        </Section>

        <Section title="Inspections">
          <table className="w-full mt-2">
            <thead><tr><TH>Date</TH><TH>Vehicle</TH><TH>Type</TH><TH>Result</TH><TH>Next due</TH></tr></thead>
            <tbody>
              {data.inspections.map((r) => (
                <tr key={r.id}>
                  <TD>{fmtDate(r.date)}</TD>
                  <TD cls="font-mono font-bold">{r.vehicle_reg}</TD>
                  <TD>{{ general: "General", tail_lift: "Tail lift", fridge: "Fridge" }[r.type] || r.type}</TD>
                  <TD cls={`uppercase font-bold ${r.result === "fail" ? "text-[#FF3B30]" : ""}`}>{r.result}</TD>
                  <TD>{fmtDate(r.next_due)}</TD>
                </tr>
              ))}
              {data.inspections.length === 0 && <tr><TD cls="text-neutral-500" colSpan={5}>No inspections recorded this week.</TD></tr>}
            </tbody>
          </table>
        </Section>

        <div className="mt-8 text-[9px] text-neutral-400 uppercase tracking-widest border-t border-neutral-300 pt-3">
          Generated {new Date().toLocaleDateString("en-GB")} · Leo's Fleet Transport Ops · Week {fmtDate(data.week_start)} — {fmtDate(data.week_end)}
        </div>
      </div>
    </div>
  );
}
